import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import type { ApiClient, LueftungFeld, LueftungVorlage } from '../../api';
import type { Rahmen } from '../Projektrahmen';
import { PageHeader } from '../../rahmen/PageHeader';
import {
  ConfirmDialog, ContentSection, DataTable, EmptyState, ErrorState, Feld, FormSection, LoadingState, PropertyGrid,
  StatusBadge, formatiereZahl, type Spalte,
} from '../../muster';
import './lueftung.css';

/**
 * Bauteilvorlagen pflegen.
 *
 * Eine Vorlage hält, was für eine Bauart immer gilt: Werkstoff einer
 * Brandschutzklappe, Messprinzip eines Volumenstromreglers, Farbe eines
 * Tellerventils. Bei 826 Brandschutzklappen desselben Fabrikats ist das der
 * Unterschied zwischen einem Nachmittag und einem Klick.
 *
 * Bewusst sparsam füllen: Ein falscher Vorgabewert ist schlimmer als keiner -
 * er sieht gepflegt aus und wird nicht mehr geprüft.
 *
 * TV6d (TX-STANDARD 1.6): nach `seiten/lueftung/` umgezogen (Route bleibt
 * `lueftung/vorlagen`). Die Liste der Vorlagen ist eine `DataTable` statt des
 * alten Knopfbaums (TX-Ausnahme „Vorlagenbaum“ entfällt); Pflege und Anwendung
 * stehen daneben. Die Felder sind die Muster-Felder wie vorher - auch das
 * Zahlfeld, das deutsch liest: Das war schon vor TV6d so und gehört zum
 * Schreibweg (`Lueftungsdaten.schreibwege.test.tsx`).
 */
export function Lueftungsvorlagen() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const { id } = useParams<{ id: string }>();

  const [vorlagen, setVorlagen] = useState<LueftungVorlage[] | null>(null);
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);

  const laden = useCallback(async () => {
    try {
      setVorlagen(await api.lueftungVorlagen());
      setFehler(null);
    } catch (e) {
      setFehler(e);
    }
  }, [api]);

  useEffect(() => { void laden(); }, [laden]);

  const vorlage = vorlagen?.find((v) => v.id === gewaehlt);

  const spalten: Spalte<LueftungVorlage>[] = useMemo(() => [
    { schluessel: 'aks', titel: 'AKS', breite: '80px', wert: (v) => v.aksCode },
    { schluessel: 'name', titel: 'Bauart', immerSichtbar: true, wert: (v) => v.name },
    { schluessel: 'bauteile', titel: 'Bauteile', art: 'zahl', nachkomma: 0, wert: (v) => v.bauteile },
    {
      schluessel: 'vorgaben', titel: 'Vorgabewerte', art: 'zahl', nachkomma: 0,
      titelHinweis: 'So viele Felder belegt die Vorlage',
      wert: (v) => vorgabenZahl(v.dataJson),
    },
  ], []);

  return (
    <>
      <PageHeader
        titel="Bauteilvorlagen Lüftung"
        beschreibung={'Eine Vorlage hält, was für eine Bauart immer gilt - Werkstoff einer Brandschutzklappe, Messprinzip '
          + 'eines Volumenstromreglers, Farbe eines Tellerventils.'}
        meta={(
          <>
            {vorlagen ? `${formatiereZahl(vorlagen.length, 0)} Vorlagen · ` : ''}
            <Link to={`/projekte/${id}/lueftung`}>← Lüftungsbestand</Link>
          </>
        )}
      />

      {fehler !== null && (
        <ErrorState meldung="Die Bauteilvorlagen konnten nicht geladen werden." technisch={fehler} beiErneutVersuchen={() => void laden()} />
      )}

      {!vorlagen && fehler === null && <LoadingState text="Bauteilvorlagen werden geladen …" zeilen={8} spalten={3} />}

      {vorlagen && (
        <div className="lue-teilung">
          <div className="lue-teilung-baum">
            <DataTable
              name="Bauteilvorlagen"
              spalten={spalten}
              zeilen={vorlagen}
              schluessel={(v) => v.id}
              beiZeileKlick={(v) => setGewaehlt(v.id)}
              aktiveZeile={gewaehlt ?? undefined}
              spaltenwahl={false}
              dicht
              suchePlatzhalter="AKS oder Bauart …"
              leer={(
                <EmptyState
                  klein
                  titel="Keine Bauteilvorlage hinterlegt."
                  erklaerung="Die Vorlagen kommen aus dem Lüftungskatalog - ohne sie gibt es nichts, was auf den Bestand angewendet werden könnte."
                />
              )}
            />
          </div>
          <div className="lue-teilung-detail">
            {vorlage
              ? <Vorlagenpflege api={api} projektId={projekt.id} vorlage={vorlage} neuLaden={() => void laden()} />
              : (
                <ContentSection titel="Vorlage">
                  <EmptyState
                    klein
                    titel="Keine Vorlage gewählt."
                    erklaerung="Links eine Bauart wählen - hier erscheinen ihre Vorgabewerte und die Anwendung auf den Bestand."
                  />
                </ContentSection>
              )}
          </div>
        </div>
      )}
    </>
  );
}

/** Wie viele Felder eine Vorlage belegt (die Schlüssel ihres JSON, wie vor TV6d). */
function vorgabenZahl(dataJson: string | null): number {
  if (!dataJson) return 0;
  try {
    return Object.keys(JSON.parse(dataJson) as object).length;
  } catch {
    return 0;
  }
}

function Vorlagenpflege({
  api, projektId, vorlage, neuLaden,
}: {
  api: ApiClient;
  projektId: string;
  vorlage: LueftungVorlage;
  neuLaden: () => void;
}) {
  const [felder, setFelder] = useState<LueftungFeld[] | null>(null);
  const [werte, setWerte] = useState<Record<string, string>>({});
  const [geaendert, setGeaendert] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gesichert, setGesichert] = useState(false);

  useEffect(() => {
    setFelder(null);
    setGeaendert(false);
    setGesichert(false);
    setFehler(null);
    setWerte(vorlage.dataJson ? JSON.parse(vorlage.dataJson) : {});

    api.lueftungFelder(vorlage.aksCode, 'bauteil')
      .then(setFelder)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, vorlage.id, vorlage.aksCode, vorlage.dataJson]);

  const sichern = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.lueftungVorlageAendern(vorlage.id, { werte });
      setGeaendert(false);
      setGesichert(true);
      neuLaden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  if (!felder) {
    return fehler
      ? <ErrorState meldung="Die Felder der Bauart konnten nicht geladen werden." technisch={fehler} />
      : <LoadingState text="Felder werden geladen …" zeilen={8} spalten={3} />;
  }

  const gruppen: { name: string; felder: LueftungFeld[] }[] = [];
  for (const f of felder) {
    const name = f.gruppe ?? 'Weitere';
    const letzte = gruppen[gruppen.length - 1];
    if (letzte?.name === name) letzte.felder.push(f);
    else gruppen.push({ name, felder: [f] });
  }

  const gesetzt = Object.values(werte).filter((w) => w?.trim()).length;

  const setzen = (schluessel: string, wert: string) => {
    setWerte((w) => ({ ...w, [schluessel]: wert }));
    setGeaendert(true);
    setGesichert(false);
  };

  return (
    <>
      <ContentSection
        titel={vorlage.name}
        beschreibung={`Vorgabewerte für alle Bauteile der Bauart ${vorlage.aksCode}.`}
        meta={(
          <>
            <StatusBadge klein zustand="neutral" text={vorlage.aksCode} />
            {vorlage.hatAntrieb && <StatusBadge klein zustand="info" text="Antrieb" titel="Bringt einen GA-Datenpunkt" />}
          </>
        )}
      >
        <PropertyGrid
          spalten={2}
          eintraege={[
            { beschriftung: 'Revit-Kategorie', wert: vorlage.revitKategorie },
            { beschriftung: 'Familienmuster', wert: vorlage.familienMuster },
            { beschriftung: 'Laufnummer', wert: `${vorlage.nummerBreite}-stellig` },
            { beschriftung: 'Bauteile im Projekt', wert: vorlage.bauteile },
          ]}
        />

        {/* Nur was für die Bauart immer gilt. Der Hinweis steht hier und nicht in einer Anleitung, weil er beim Ausfüllen zählt. */}
        <p className="lue-leise">
          Nur ausfüllen, was für <strong>alle</strong> Bauteile dieser Bauart gilt. Ein falscher Vorgabewert ist schlimmer
          als keiner - er sieht gepflegt aus. Derzeit {formatiereZahl(gesetzt, 0)} von {formatiereZahl(felder.length, 0)} Feldern belegt.
        </p>

        {gruppen.map((g) => (
          <FormSection key={g.name} titel={g.name} spalten={3}>
            {g.felder.map((f) => <Vorgabefeld key={f.fieldKey} feld={f} wert={werte[f.fieldKey]} setzen={setzen} />)}
          </FormSection>
        ))}

        {fehler && (
          <ErrorState
            klein
            titel="Die Vorlage konnte nicht gespeichert werden."
            meldung={fehler}
            beiErneutVersuchen={() => void sichern()}
          />
        )}

        <div className="lue-speichern">
          <button type="button" className="m-knopf m-knopf-haupt" disabled={!geaendert || laeuft} onClick={() => void sichern()}>
            {laeuft ? 'speichert …' : 'Vorlage speichern'}
          </button>
          <span className="lue-meldung" role="status">{gesichert ? 'gespeichert' : ''}</span>
        </div>
      </ContentSection>

      <Anwenden api={api} projektId={projektId} vorlage={vorlage} />
    </>
  );
}

/** Ein Vorgabefeld je Datentyp - wie vor TV6d, mit dem Muster-Feld. */
function Vorgabefeld({ feld: f, wert, setzen }: {
  feld: LueftungFeld;
  wert: string | undefined;
  setzen: (schluessel: string, wert: string) => void;
}) {
  if (f.dataType === 'ja_nein' || (f.dataType === 'auswahl' && f.options)) {
    const optionen = f.dataType === 'ja_nein'
      ? [{ wert: 'true', beschriftung: 'ja' }, { wert: 'false', beschriftung: 'nein' }]
      : f.options!.split('|').map((o) => ({ wert: o, beschriftung: o }));
    return (
      <Feld art="auswahl" beschriftung={f.label} pflicht={f.istPflicht} wert={wert ?? ''}
        optionen={[{ wert: '', beschriftung: '—' }, ...optionen]}
        beiAenderung={(w) => setzen(f.fieldKey, w as string)} />
    );
  }
  if (f.dataType === 'zahl') {
    return (
      <Feld art="zahl" beschriftung={f.label} einheit={f.unit ?? undefined} pflicht={f.istPflicht}
        wert={wert === undefined || wert === '' ? null : Number(wert)}
        beiAenderung={(w) => setzen(f.fieldKey, w === null ? '' : String(w))} />
    );
  }
  return (
    <Feld beschriftung={f.label} einheit={f.unit ?? undefined} pflicht={f.istPflicht} wert={wert ?? ''}
      beiAenderung={(w) => setzen(f.fieldKey, w as string)} />
  );
}

/**
 * Eine Vorlage auf den Bestand anwenden.
 *
 * Zwei Schritte: erst zählen, dann setzen. Bei 826 Brandschutzklappen soll vor
 * dem Klick die Zahl stehen - und nicht danach die Frage, was gerade passiert ist.
 */
function Anwenden({ api, projektId, vorlage }: { api: ApiClient; projektId: string; vorlage: LueftungVorlage }) {
  const [ueberschreiben, setUeberschreiben] = useState(false);
  const [frage, setFrage] = useState(false);
  const [zahl, setZahl] = useState<number | null>(null);
  const [ergebnis, setErgebnis] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const zaehlen = async () => {
    setLaeuft(true); setFehler(null); setErgebnis(null);
    try {
      const a = await api.lueftungVorlageAnwenden(vorlage.id, { projectId: projektId, ueberschreiben, nurZaehlen: true });
      setZahl(a.betroffen);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const anwenden = async () => {
    setLaeuft(true); setFehler(null);
    try {
      const a = await api.lueftungVorlageAnwenden(vorlage.id, { projectId: projektId, ueberschreiben, nurZaehlen: false });
      setErgebnis(`${a.betroffen} Bauteil(e) geändert, ${a.uebersprungen} Werte übersprungen.`);
      setZahl(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <ContentSection
      titel="Auf den Bestand anwenden"
      beschreibung="Zwei Schritte: erst zählen, dann setzen. Bei 826 Brandschutzklappen soll die Zahl VOR dem Klick stehen."
    >
      {vorlage.bauteile === 0 && (
        <p className="lue-leise">
          <StatusBadge klein zustand="warnung" text="kein Bauteil" /> Diese Vorlage trägt derzeit kein Bauteil im Projekt.
          Möglich ist, dass das Familienmuster nicht greift.
        </p>
      )}

      <Feld art="haken" beschriftung="Vorhandene Werte überschreiben" wert={ueberschreiben}
        hinweis="Von Hand gepflegte Werte bleiben immer unberührt, auch mit Haken. Nur leere Felder und frühere Vorlagenwerte werden gesetzt."
        beiAenderung={(w) => { setUeberschreiben(w as boolean); setZahl(null); }} />

      {fehler && (
        <ErrorState klein titel="Die Vorlage konnte nicht angewendet werden." meldung={fehler} beiErneutVersuchen={() => void zaehlen()} />
      )}

      <p className="lue-meldung" role="status">
        {ergebnis ?? (zahl !== null
          ? `${zahl} Bauteil(e) tragen diese Vorlage. ${zahl > 0 ? 'Beim Anwenden werden ihre Felder gesetzt.' : 'Es gibt nichts zu tun.'}`
          : '')}
      </p>

      <div className="lue-speichern">
        <button type="button" className="m-knopf" disabled={laeuft} onClick={() => void zaehlen()}>
          {laeuft ? 'läuft …' : 'Zählen'}
        </button>
        <button type="button" className="m-knopf m-knopf-haupt" disabled={laeuft || zahl === null || zahl === 0} onClick={() => setFrage(true)}>
          {zahl !== null ? `Auf ${zahl} Bauteil(e) anwenden` : 'Erst zählen'}
        </button>
      </div>

      <ConfirmDialog
        offen={frage}
        art="warnung"
        titel={`Vorlage auf ${zahl ?? 0} Bauteil(e) anwenden?`}
        text={ueberschreiben
          ? 'Vorhandene Vorlagenwerte werden überschrieben. Von Hand gepflegte Werte bleiben unberührt.'
          : 'Nur leere Felder werden gesetzt.'}
        bestaetigenText="Anwenden"
        laeuft={laeuft}
        beiBestaetigen={() => { setFrage(false); void anwenden(); }}
        beiAbbrechen={() => setFrage(false)}
      />
    </ContentSection>
  );
}
